import {
  IFeeStructureResponse,
  IFeeCategory,
  IFeeComponentDefinition,
} from '../apis/institute/institute.interfaces';

const defaultSchoolItems: IFeeComponentDefinition[] = [
  { componentId: 'bag', componentName: 'School Bag', amount: 800, isOptional: true },
  { componentId: 'books', componentName: 'Books', amount: 2500, isOptional: true },
  { componentId: 'uniform', componentName: 'Uniform', amount: 1200, isOptional: true },
  { componentId: 'shoes', componentName: 'Shoes', amount: 600, isOptional: true },
];

const defaultTuition: IFeeComponentDefinition[] = [
  { componentId: 'tuition', componentName: 'Tuition Fees', amount: 45000, isOptional: false },
];

const defaultTransport: IFeeComponentDefinition[] = [
  { componentId: 'transport', componentName: 'Transport Fee', amount: 12000, isOptional: true },
];

const defaultFeeCategories: IFeeCategory[] = [
  {
    categoryId: 'cat-tuition',
    categoryName: 'Tuition',
    categoryDescription: 'Mandatory tuition fees',
    precedence: 1,
    components: defaultTuition,
  },
  {
    categoryId: 'cat-school-items',
    categoryName: 'School Items',
    categoryDescription: 'Optional school supplies',
    precedence: 2,
    components: defaultSchoolItems,
  },
  {
    categoryId: 'cat-transport',
    categoryName: 'Transport',
    categoryDescription: 'Optional transport fee',
    precedence: 3,
    components: defaultTransport,
  },
];

/** Scholarship category: lower tuition, no transport */
const scholarshipFeeCategories: IFeeCategory[] = [
  {
    categoryId: 'cat-tuition',
    categoryName: 'Tuition',
    precedence: 1,
    components: [
      { componentId: 'tuition', componentName: 'Tuition Fees (Scholarship)', amount: 25000, isOptional: false },
    ],
  },
  {
    categoryId: 'cat-school-items',
    categoryName: 'School Items',
    precedence: 2,
    components: defaultSchoolItems,
  },
];

/** Grade 1 Section A: standard structure */
const grade1SectionARegular: IFeeStructureResponse = defaultFeeCategories;

/** Grade 1 Section A Scholarship */
const grade1SectionAScholarship: IFeeStructureResponse = scholarshipFeeCategories;

/** Grade 2+ Section B: higher tuition */
const grade2SectionBRegular: IFeeStructureResponse = [
  {
    categoryId: 'cat-tuition',
    categoryName: 'Tuition',
    precedence: 1,
    components: [
      { componentId: 'tuition', componentName: 'Tuition Fees', amount: 52000, isOptional: false },
    ],
  },
  {
    categoryId: 'cat-school-items',
    categoryName: 'School Items',
    precedence: 2,
    components: defaultSchoolItems,
  },
  {
    categoryId: 'cat-transport',
    categoryName: 'Transport',
    precedence: 3,
    components: defaultTransport,
  },
];

type MockKey = string;

const buildKey = (grade: string, section: string, category: string): MockKey =>
  `${grade}|${section}|${category}`;

const feeStructureMockMap: Record<MockKey, IFeeStructureResponse> = {
  [buildKey('1', 'A', 'REGULAR')]: grade1SectionARegular,
  [buildKey('1', 'A', 'SCHOLARSHIP')]: grade1SectionAScholarship,
  [buildKey('GRADE_1', 'A', 'REGULAR')]: grade1SectionARegular,
  [buildKey('GRADE_1', 'A', 'SCHOLARSHIP')]: grade1SectionAScholarship,
  [buildKey('2', 'B', 'REGULAR')]: grade2SectionBRegular,
  [buildKey('GRADE_2', 'B', 'REGULAR')]: grade2SectionBRegular,
};

/**
 * Returns fee structure for given grade, section, category.
 * Falls back to default structure when no exact match exists.
 */
export const getFeeStructureMock = (
  grade: string,
  section: string,
  category: string
): IFeeStructureResponse => {
  const key = buildKey(grade, section, category);
  return feeStructureMockMap[key] ?? defaultFeeCategories;
};
